import { useState } from 'react';

import type { AgentSummary } from './AgentsView';

interface Props {
  agent: AgentSummary;
  selected: boolean;
  onClick: () => void;
}

export function AgentCard({ agent, selected, onClick }: Props) {
  const [hovered, setHovered] = useState(false);
  const deactivated = agent.status === 'deactivated';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: 'var(--surface)',
        // DESIGN_DEVIATION: paper-grain CSS texture (≤3% opacity) layered over surface — no
        // Skills/Team parallel; brief explicitly requested one tiny CSS texture detail per card.
        backgroundImage: PAPER_GRAIN,
        backgroundSize: '6px 6px',
        border: `1px solid ${selected ? 'var(--accent)' : hovered ? 'var(--border-strong)' : 'var(--border)'}`,
        borderRadius: 8,
        padding: '18px 18px 16px',
        cursor: 'pointer',
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'transform 140ms ease, border-color 140ms ease',
        opacity: deactivated ? 0.66 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        outline: 'none',
      }}
    >
      <RoleStamp roleName={agent.role.name} />

      <div>
        <div
          style={{
            fontFamily: HEADING_SERIF,
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.15,
            color: 'var(--text)',
            letterSpacing: '-0.012em',
            textDecoration: deactivated ? 'line-through' : 'none',
            textDecorationColor: 'var(--text-soft)',
            textDecorationThickness: '1px',
          }}
        >
          {agent.name}
        </div>
        <div
          style={{
            marginTop: 6,
            fontFamily: MONO,
            fontSize: 11,
            color: 'var(--text-soft)',
            letterSpacing: '0.02em',
          }}
        >
          @{agent.slug}
        </div>
      </div>

      {agent.description && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--text-mute)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {agent.description}
        </div>
      )}

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          paddingTop: 12,
          borderTop: '1px dashed var(--border)',
        }}
      >
        <StatusIndicator status={agent.status} />
        <FileFlag label="AGENT.md" present={agent.has_agent_md} />
        <FileFlag label="SOUL.md" present={agent.has_soul_md} />
        <div
          style={{
            marginLeft: 'auto',
            fontFamily: MONO,
            fontSize: 10,
            color: 'var(--text-soft)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
          title={`${agent.skill_count} skill${agent.skill_count === 1 ? '' : 's'} granted via role`}
        >
          {agent.skill_count.toString().padStart(2, '0')}&nbsp;skills
        </div>
      </div>
    </div>
  );
}

function RoleStamp({ roleName }: { roleName: string }) {
  return (
    <div
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        background: 'var(--bg-alt)',
        border: '1px solid var(--border)',
        borderRadius: 3,
        fontFamily: MONO,
        fontSize: 9.5,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        color: 'var(--text-mute)',
        // pressed-metal feel
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.04)',
      }}
    >
      {roleName}
    </div>
  );
}

function StatusIndicator({ status }: { status: 'active' | 'deactivated' }) {
  const active = status === 'active';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: active ? 'var(--kind-success)' : 'var(--text-soft)',
          boxShadow: active ? '0 0 0 2px color-mix(in srgb, var(--kind-success) 20%, transparent)' : 'none',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontFamily: MONO,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: active ? 'var(--text-mute)' : 'var(--text-soft)',
        }}
      >
        {active ? 'Active' : 'Off-duty'}
      </span>
    </div>
  );
}

function FileFlag({ label, present }: { label: string; present: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: MONO,
        fontSize: 10,
        letterSpacing: '0.04em',
        color: present ? 'var(--text-mute)' : 'var(--text-soft)',
      }}
      title={present ? `${label} present` : `${label} not present`}
    >
      <span
        style={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: present ? 'var(--text-mute)' : 'transparent',
          border: present ? 'none' : '1px solid var(--text-soft)',
          flexShrink: 0,
        }}
      />
      {label}
    </div>
  );
}

const HEADING_SERIF =
  "ui-serif, Georgia, 'Iowan Old Style', 'Apple Garamond', Palatino, serif";
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Paper-grain via two intersecting repeating-linear-gradients at ≤3% opacity.
const PAPER_GRAIN =
  'repeating-linear-gradient(45deg, rgba(0,0,0,0.018) 0 1px, transparent 1px 6px),' +
  'repeating-linear-gradient(-45deg, rgba(0,0,0,0.012) 0 1px, transparent 1px 7px)';
